import { makeStyles } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import React, { useId } from 'react';

import { getFDS } from '../fdsTokens';

/**
 * The one definition of a field label's type.
 *
 * 12px is the design system's own `--fs-caption`, at `--fw-medium` — the nearest
 * defined step to the 11.5px `microLabel` this container already used above its
 * Choices / Formulas editors, which moved to 12 with it so an expanded row
 * carries ONE label style rather than two half-pixels apart (AXHE-4384 §4).
 *
 * Exported because one surface cannot use the component: a radio pair's label has
 * to stay a real `<legend>` (see `SendOfferForm`'s `radioLabel`), and a copied
 * block of values there would drift the moment this one is edited, with nothing
 * to signal it.
 */
export const fieldLabelStyle = (theme: Theme): CSSProperties => ({
  display: 'block',
  marginBottom: 4,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  color: getFDS(theme).fgSecondary,
});

const useStyles = makeStyles(theme => ({
  label: fieldLabelStyle(theme),
}));

/**
 * A stable, unique id for one component instance, for label association.
 * `useId` returns colons, which a CSS `#id` selector cannot hold unescaped.
 */
export const useFieldId = (): string =>
  `olt-field-${useId().replace(/:/g, '')}`;

interface Props {
  /**
   * The control's own `id`. Use for anything that resolves to a real `<input>`
   * — text fields and Autocomplete (whose `id` prop lands on its input).
   */
  htmlFor?: string;
  /**
   * The LABEL's id, to be passed back as a Select's `labelId`. MUI v4 renders a
   * select as a `div[role="button"]` plus an `aria-hidden` native input, and an
   * `id` handed to `TextField` lands on the *hidden* one — so `htmlFor` would
   * point the label at something nobody can click or read. `labelId` puts
   * `aria-labelledby` on the display element instead, which is both the
   * accessible name and what `getByLabelText` resolves to.
   */
  id?: string;
  children: React.ReactNode;
}

/**
 * A field label sitting ABOVE the control's border — Hire's convention, and
 * what the rail's expanded rows were missing: MUI's outlined `label` prop draws
 * the label INTO the fieldset legend instead (AXHE-4384 §4).
 *
 * Same shape as `ChangeWorkflowModal/WorkflowSelect` and
 * `ChangeOpeningStatusModal/OpeningStatusSelect`, which put an `InputLabel`
 * outside the `FormControl` for exactly this. Rendered here as the bare
 * elements those resolve to, because MUI v4 types `InputLabel` without a
 * `component` prop and the two branches below need different elements.
 *
 * Pass exactly one of `htmlFor` / `id` — see their docs for which.
 */
export const FieldLabel: React.FC<Props> = ({ htmlFor, id, children }) => {
  const classes = useStyles();

  // A `<label>` with no `for` labels nothing, and wrapping the control is not
  // an option for a select (its clickable element is nested deep), so that
  // branch renders a plain span and is associated from the other end by
  // `aria-labelledby`.
  return htmlFor ? (
    <label className={classes.label} htmlFor={htmlFor}>
      {children}
    </label>
  ) : (
    <span className={classes.label} id={id}>
      {children}
    </span>
  );
};

export default FieldLabel;
